import React, { useState, useEffect } from 'react';
import Card from './Card';
import Button from './Button';
import CloseIcon from '../icons/CloseIcon';

interface ItemUsageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** 승인 요청을 보내는 중이면 버튼을 막아 중복 요청을 막는다 */
  isSubmitting?: boolean;
  item: {
    id: string;
    name?: string;
    description?: string;
    itemCategory?: string;
    image?: string;
    purchasedAt?: any;
  } | null;
}

const ItemUsageDialog: React.FC<ItemUsageDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  item
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isOpen || !item) return null;

  const formatDate = (date: any) => {
    if (!date) return '2025.05.25';
    
    try {
      if (date.toDate && typeof date.toDate === 'function') {
        return date.toDate().toLocaleDateString('ko-KR');
      } else if (date instanceof Date) {
        return date.toLocaleDateString('ko-KR');
      } else if (typeof date === 'string') {
        return new Date(date).toLocaleDateString('ko-KR');
      }
      return '2025.05.25';
    } catch (error) {
      return '2025.05.25';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: 'Pretendard',
      padding: isMobile ? '16px' : '0'
    }}>
      <Card
        width={isMobile ? '100%' : '450px'}
        padding="0"
        borderRadius="16px"
        background="var(--Color-background-normal, white)"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
        style={{
          position: 'relative',
          overflow: 'hidden',
          maxWidth: isMobile ? '100%' : '450px',
          maxHeight: isMobile ? '90vh' : 'none',
          overflowY: isMobile ? 'auto' : 'visible'
        }}
      >
        <div style={{
          width: isMobile ? 'calc(100% - 32px)' : '394px',
          margin: isMobile ? '16px' : '28px',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '24px' : '40px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <h2 style={{
                margin: 0,
                color: 'var(--Color-text-normal, #161616)',
                fontSize: isMobile ? '18px' : '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: isMobile ? '27px' : '30px'
              }}>
                아이템 사용하기
              </h2>
            </div>
            
            {/* 닫기 버튼 */}
            <div 
              style={{
                padding: '4px',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={onClose}
            >
              <CloseIcon width={13.20} height={13.20} color="#0D0D0D" />
            </div>
          </div>

          {/* 아이템 정보 섹션 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '16px' : '20px'
          }}>
            {/* 아이템 이미지 및 정보 */}
            <div style={{
              height: isMobile ? 'auto' : '346px',
              minHeight: isMobile ? 'auto' : '346px',
              padding: isMobile ? '12px 16px' : '16px 20px',
              background: 'var(--Color-background-sub, #F8F8F8)',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: isMobile ? '12px' : '16px'
            }}>
              <div style={{
                alignSelf: 'stretch',
                textAlign: 'center',
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: isMobile ? '12px' : '14px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: isMobile ? '18px' : '21px'
              }}>
                아이템 정보
              </div>
              
              {/* 아이템 이미지 */}
              <div style={{
                width: isMobile ? '100%' : '224px',
                maxWidth: isMobile ? '100%' : '224px',
                height: isMobile ? '150px' : '185px',
                background: '#D9D9D9',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {item.image && item.image !== 'https://via.placeholder.com/150' ? (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '8px'
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{
                    color: 'var(--Color-text-assistive, #989898)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500'
                  }}>
                    이미지 없음
                  </div>
                )}
              </div>

              {/* 아이템 제목과 설명 */}
              <div style={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '16px' : '28px'
              }}>
                <div style={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '12px' : '24px'
                }}>
                  <div style={{
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '14px' : '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: isMobile ? '21px' : '24px'
                    }}>
                      {item.name || '언제나 멋져요 아이템'}
                    </div>
                    <div style={{
                      alignSelf: 'stretch',
                      color: 'var(--Color-text-normal, #161616)',
                      fontSize: isMobile ? '13px' : '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: isMobile ? '19.5px' : '24px'
                    }}>
                      {item.description || '해당 아이템에 대한 정보가 노출되는 구간입니다. 최대 2줄까지 보여질 예정으로'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 유형 및 구매일 정보 */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'flex-start',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '12px' : '24px'
            }}>
              <div style={{
                width: isMobile ? '100%' : '185px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: isMobile ? '12px' : '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: isMobile ? '18px' : '21px'
                }}>
                  유형
                </div>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: isMobile ? '14px' : '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: isMobile ? '21px' : '24px'
                }}>
                  {item.itemCategory === 'qr' ? 'QR코드' : '사용권'}
                </div>
              </div>
              <div style={{
                width: isMobile ? '100%' : '185px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div style={{
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: isMobile ? '12px' : '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '600',
                  lineHeight: isMobile ? '18px' : '21px'
                }}>
                  구매일
                </div>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: isMobile ? '14px' : '16px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  lineHeight: isMobile ? '21px' : '24px'
                }}>
                  {formatDate(item.purchasedAt)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          padding: isMobile ? '0 16px 16px 16px' : '0 28px 28px 28px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: isMobile ? '8px' : '8px'
        }}>
          <Button
            width={isMobile ? '100%' : '124px'}
            height={isMobile ? '44px' : '48px'}
            variant="cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            취소
          </Button>
          <Button
            width={isMobile ? '100%' : '262px'}
            height={isMobile ? '44px' : '48px'}
            variant="submit"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? '요청 보내는 중...' : '사용 승인 요청'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ItemUsageDialog;
